import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAiActionPlan, fetchAiInsightsData } from '../../lib/api';
import { useUiStore } from '../../store/ui';
import { safeString } from '../../lib/format';
import type { AiActionItem } from '../../lib/types';
import { useBusinessOsNavigation } from '../shared/navigation';

function contextForPath(pathname: string): string {
  if (pathname.startsWith('/pipeline')) return 'pipeline';
  if (pathname.startsWith('/missions')) return 'missions';
  if (pathname.startsWith('/factures')) return 'factures';
  if (pathname.startsWith('/affectation')) return 'production';
  if (pathname.startsWith('/finance')) return 'finance';
  return 'dashboard';
}

function filterInsightsForContext(data: any, context: string) {
  const allActions = Array.isArray(data?.next_best_actions) ? data.next_best_actions : [];
  const actions = allActions.filter((a: any) => {
    const tab = safeString(a.target_tab).toLowerCase();
    if (context === 'dashboard') return true;
    if (context === 'pipeline') return tab === 'pipeline' || tab === 'opportunites';
    if (context === 'missions') return tab === 'missions';
    if (context === 'factures') return tab === 'factures' || tab === 'finance_ops';
    if (context === 'production') return tab === 'production' || tab === 'affectation';
    if (context === 'finance') return tab === 'finance_ops' || tab === 'factures';
    return true;
  });
  const risks = (Array.isArray(data?.risk_flags) ? data.risk_flags : []).slice(0, 3);
  const conflicts = (Array.isArray(data?.capacity_conflicts) ? data.capacity_conflicts : []).slice(0, 3);
  return { actions: actions.slice(0, 5), risks, conflicts };
}

function filterActionPlanForContext(items: AiActionItem[], context: string) {
  const safeItems = Array.isArray(items) ? items : [];
  return safeItems.filter((item) => {
    const tab = safeString(item.target_tab || item.target?.target_tab).toLowerCase();
    if (context === 'dashboard') return true;
    if (context === 'pipeline') return tab === 'pipeline' || tab === 'opportunites';
    if (context === 'missions') return tab === 'missions';
    if (context === 'factures') return tab === 'factures' || tab === 'finance_ops';
    if (context === 'production') return tab === 'production' || tab === 'affectation';
    if (context === 'finance') return tab === 'finance_ops' || tab === 'factures';
    return true;
  }).slice(0, 4);
}

export function AiSidebar() {
  const open = useUiStore((s) => s.aiSidebarOpen);
  const setOpen = useUiStore((s) => s.setAiSidebarOpen);
  const toggleOpen = useUiStore((s) => s.toggleAiSidebar);
  const openTarget = useBusinessOsNavigation();
  const location = useLocation();
  const selectedYear = useUiStore((s) => s.selectedYear);
  const context = contextForPath(location.pathname);
  const [panelReady, setPanelReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setPanelReady(false);
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      setPanelReady(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const actionsQuery = useQuery({
    queryKey: ['ai-actions', selectedYear],
    queryFn: () => fetchAiActionPlan(selectedYear),
    enabled: open && panelReady,
    staleTime: 60_000,
  });
  const query = useQuery({
    queryKey: ['ai-insights', selectedYear],
    queryFn: () => fetchAiInsightsData(selectedYear),
    enabled: open && panelReady && actionsQuery.isSuccess,
    staleTime: 60_000,
  });

  const filtered = query.data ? filterInsightsForContext(query.data, context) : null;
  const actionItems = useMemo(
    () => {
      const direct = actionsQuery.data?.actions || [];
      const fallback = actionsQuery.data?.sections
        ? [
          ...(actionsQuery.data.sections.high_priority || []),
          ...(actionsQuery.data.sections.quick_wins || []),
          ...(actionsQuery.data.sections.review_queue || []),
        ]
        : [];
      return filterActionPlanForContext(direct.length > 0 ? direct : fallback, context);
    },
    [actionsQuery.data, context],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        toggleOpen();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggleOpen]);

  return (
    <>
      <button
        type="button"
        className={`ai-sidebar-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen(!open)}
        title="IA contextuelle (Ctrl+I)"
        aria-label="Ouvrir le panneau IA"
      >
        ✦
      </button>

      {open && (
        <>
          <button type="button" className="ai-sidebar-backdrop" aria-label="Fermer le panneau IA" onClick={() => setOpen(false)} />
          <aside className="ai-sidebar">
            <div className="ai-sidebar__header">
              <span className="ai-sidebar__title">IA · {context}</span>
              <button type="button" className="ai-sidebar__close" onClick={() => setOpen(false)}>&times;</button>
            </div>

            <div className="ai-sidebar__body">
              {!panelReady || query.isLoading || actionsQuery.isLoading ? <div className="ai-sidebar__loading">Analyse en cours...</div> : null}
              {(query.isError || actionsQuery.isError) && <div className="ai-sidebar__empty">Erreur de chargement. <button type="button" className="button button--ghost" style={{ marginTop: '0.5rem' }} onClick={() => { void query.refetch(); void actionsQuery.refetch(); }}>Réessayer</button></div>}

              {panelReady && actionItems.length > 0 && (
                <div className="ai-sidebar__section">
                  <h4 className="ai-sidebar__section-title">Action Center</h4>
                  {actionItems.map((action) => (
                    <div key={safeString(action.id)} className="ai-sidebar__item">
                      <span className={`ai-sidebar__severity ai-sidebar__severity--${safeString(action.priority || action.risk_level)}`} />
                      <div>
                        <strong>{safeString(action.title)}</strong>
                        <p>{safeString(action.reason || action.explanation)}</p>
                        <button
                          type="button"
                          className="button button--ghost"
                          style={{ marginTop: '0.4rem', fontSize: 'var(--text-xs)' }}
                          onClick={() => {
                            if (!action.target?.route || !action.target.view) return;
                            openTarget({
                              route: safeString(action.target.route),
                              view: safeString(action.target.view) as never,
                              filters: (action.target.filters || {}) as Record<string, string>,
                            });
                          }}
                        >
                          Ouvrir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {panelReady && filtered ? (
                <>
                  {actionItems.length === 0 && filtered.actions.length > 0 && (
                    <div className="ai-sidebar__section">
                      <h4 className="ai-sidebar__section-title">Actions recommandees</h4>
                      {filtered.actions.map((a: any, i: number) => (
                        <div key={i} className="ai-sidebar__item">
                          <span className={`ai-sidebar__severity ai-sidebar__severity--${safeString(a.severity)}`} />
                          <div>
                            <strong>{safeString(a.title)}</strong>
                            <p>{safeString(a.explanation)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filtered.risks.length > 0 && (
                    <div className="ai-sidebar__section">
                      <h4 className="ai-sidebar__section-title">Risques</h4>
                      {filtered.risks.map((r: any, i: number) => (
                        <div key={i} className="ai-sidebar__item">
                          <span className={`ai-sidebar__severity ai-sidebar__severity--${safeString(r.severity)}`} />
                          <div>
                            <strong>{safeString(r.label)}</strong>
                            <span className="ai-sidebar__value">{safeString(r.value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filtered.conflicts.length > 0 && context !== 'factures' && context !== 'finance' && (
                    <div className="ai-sidebar__section">
                      <h4 className="ai-sidebar__section-title">Conflits capacite</h4>
                      {filtered.conflicts.map((c: any, i: number) => (
                        <div key={i} className="ai-sidebar__item">
                          <div>
                            <strong>{safeString(c.label || c.month)}</strong>
                            <span className="ai-sidebar__value">{safeString(c.overload_days)} j surcharge</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {actionItems.length === 0 && filtered.actions.length === 0 && filtered.risks.length === 0 && (
                    <div className="ai-sidebar__empty">Aucun signal pour cette vue.</div>
                  )}
                </>
              ) : null}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
